import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Award, Briefcase, Calculator, ChevronLeft, DollarSign, Loader2, Megaphone, Save, Search, Shield, Truck, Users } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const ServicesHubPage = () => {
  const [directory, setDirectory] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/services/directory`);
        setDirectory(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);
  
  const networkCards = [
    { id: "attorney-network", name: "Attorney Network", icon: Briefcase, desc: "Nationwide bar-admitted attorneys for appearances, depositions, and case support", color: "from-blue-600 to-blue-800" },
    { id: "expert-network", name: "Expert Witness Network", icon: Award, desc: "Court experts across medical, engineering, financial, and technical specialties", color: "from-purple-600 to-purple-800" },
    { id: "investigation-services", name: "Investigation Services", icon: Search, desc: "Background checks, surveillance, asset searches, and deep investigations", color: "from-red-600 to-red-800" },
    { id: "insurance-analytics", name: "Insurance Analytics", icon: Shield, desc: "Carrier analytics, payout formulas, bad faith tracking, and claims valuation", color: "from-emerald-600 to-emerald-800" },
    { id: "paralegal-network", name: "Paralegal Network", icon: Users, desc: "Offshore economy to onshore premium paralegal staffing", color: "from-cyan-600 to-cyan-800" },
    { id: "courier-services", name: "Legal Courier", icon: Truck, desc: "Nationwide same-day legal document delivery via Uber Direct", color: "from-amber-600 to-amber-800" },
    { id: "legal-financing", name: "Legal Financing", icon: DollarSign, desc: "Pre-settlement funding, case cost financing, and law firm loans", color: "from-green-600 to-green-800" },
    { id: "marketing-services", name: "Marketing Services", icon: Megaphone, desc: "PPC, SEO, content marketing, and client acquisition programs", color: "from-pink-600 to-pink-800" }
  ];
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="services-hub-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-7xl">
        <div className="mb-8">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Services Hub</h1>
            <Badge className="bg-gradient-to-r from-slate-700 to-slate-900">WHITE LABEL</Badge>
          </div>
          <p className="text-muted-foreground text-lg">Revenue-generating service networks - all integrated into your practice</p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Network Cards Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {networkCards.map(net => (
                <Link key={net.id} to={`/tools/${net.id}`}>
                  <Card className="h-full hover:shadow-lg transition-all cursor-pointer group overflow-hidden">
                    <div className={`h-2 bg-gradient-to-r ${net.color}`} />
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-lg bg-gradient-to-r ${net.color} text-white`}>
                          <net.icon className="h-5 w-5" />
                        </div>
                        <h3 className="font-semibold group-hover:text-blue-600 transition-colors">{net.name}</h3>
                      </div>
                      <p className="text-sm text-muted-foreground">{net.desc}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            
            {/* Quick Stats */}
            {directory && (
              <Card className="mb-8">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Integrated Data Sources & Partners</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <h4 className="font-medium text-sm text-muted-foreground mb-2">COURT DATA</h4>
                      <div className="flex flex-wrap gap-2">
                        {directory.databases_and_integrations?.court_data?.map((d, i) => (
                          <Badge key={i} variant="outline">{d}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-sm text-muted-foreground mb-2">FINANCING PARTNERS</h4>
                      <div className="flex flex-wrap gap-2">
                        {directory.databases_and_integrations?.financing?.map((d, i) => (
                          <Badge key={i} variant="outline">{d}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium text-sm text-muted-foreground mb-2">INSURANCE DATA</h4>
                      <div className="flex flex-wrap gap-2">
                        {directory.databases_and_integrations?.insurance_data?.map((d, i) => (
                          <Badge key={i} variant="outline">{d}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* ROI Calculator */}
            <Card>
              <CardHeader>
                <CardTitle>Cost Comparison: Traditional vs Trialeze</CardTitle>
                <CardDescription>See how much you save with our integrated platform</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                    <h4 className="font-semibold text-red-800 mb-4">Traditional Approach (Monthly)</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span>Westlaw/Lexis</span><span>$300+</span></div>
                      <div className="flex justify-between"><span>LexMachina Analytics</span><span>$1,000+</span></div>
                      <div className="flex justify-between"><span>Virtual Paralegal (10 hrs)</span><span>$350</span></div>
                      <div className="flex justify-between"><span>Investigation Services</span><span>$500+</span></div>
                      <div className="flex justify-between"><span>Expert Witness Retainer</span><span>$1,000+</span></div>
                      <Separator className="my-2" />
                      <div className="flex justify-between font-bold text-red-800"><span>Total</span><span>$3,150+/mo</span></div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                    <h4 className="font-semibold text-emerald-800 mb-4">Trialeze Professional (Monthly)</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span>All AI Analytics Tools (31+)</span><span className="text-emerald-600">Included</span></div>
                      <div className="flex justify-between"><span>Trellis + PACER + CourtListener</span><span className="text-emerald-600">Included</span></div>
                      <div className="flex justify-between"><span>10 hrs Virtual Paralegal</span><span className="text-emerald-600">Included</span></div>
                      <div className="flex justify-between"><span>3 Basic Investigations</span><span className="text-emerald-600">Included</span></div>
                      <div className="flex justify-between"><span>Insurance Analytics</span><span className="text-emerald-600">Included</span></div>
                      <Separator className="my-2" />
                      <div className="flex justify-between font-bold text-emerald-800"><span>Total</span><span>$499/mo</span></div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 p-4 rounded-lg bg-blue-50 border border-blue-200 text-center">
                  <p className="text-2xl font-bold text-blue-800">Save $2,651+ per month</p>
                  <p className="text-blue-600">$31,812+ annual savings per attorney</p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default ServicesHubPage;
